import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  Plus,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Grid3X3,
  List,
  Play,
  Pause,
  Trash2,
  Eye,
  MoreVertical,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  Mail,
  Activity,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CampaignControls } from './CampaignControls';
import {
  useHermesCampaigns,
  useHermesCampaignJobs,
  useUpdateHermesCampaign,
  useDeleteHermesCampaign
} from '@/hooks/useHermesCampaigns';
import { computeCampaignJobCounts } from '@/lib/email/job-status';
import { resolveCampaignTotals } from '@/lib/email/campaign-totals';
import { useResumeCampaign } from '@/hooks/useEmailBackend';
import { useToast } from '@/hooks/use-toast';
// Keep FastAPI hooks for processing/monitoring only - not used in campaign cards
// import { 
//   useEmailCampaignProgress
//   // useSafetyStatus - temporarily disabled due to CORS 
// } from '@/hooks/useEmailBackend';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { campaignWizardAtom } from '@/lib/atoms';
import { track } from '@/lib/analytics';

interface CampaignCardProps {
  campaign: any;
  isSelected: boolean;
  onSelect: (id: string, selected: boolean) => void;
  onDelete: (id: string) => void;
  viewMode: 'grid' | 'list';
}

function CampaignCard({ campaign, isSelected, onSelect, onDelete, viewMode }: CampaignCardProps) {
  const navigate = useNavigate();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // Per-job rows still let us split `emails_failed` into "real failures" vs.
  // "skipped (recipient suppressed)" — the campaign record alone can't
  // distinguish them. See src/lib/email/job-status.ts header for context.
  // Only enabled for campaigns that have actually attempted sends; drafts
  // and scheduled campaigns skip the query.
  const jobsEnabled = !['draft', 'scheduled'].includes(campaign.status);
  const { data: jobs = [], isSuccess: jobsLoaded } = useHermesCampaignJobs(
    jobsEnabled ? campaign.id : undefined,
  );
  const jobCounts = useMemo(() => computeCampaignJobCounts(jobs), [jobs]);

  // Totals (recipients/sent/failed/skipped/completionPercentage) come from the durable
  // counters on the campaign row, never from these job rows: hermes_email_queue_jobs is
  // purged 7 days after sending, so a total derived from surviving rows decays daily and
  // can undercount or read 0 (#859). jobCounts is still passed through so the resolver can
  // split emails_failed into real failures vs. suppression skips for the surviving window —
  // but only once the query has settled, so an unsettled window is never mistaken for a
  // purged one.
  // isSuccess, not !isLoading: react-query v5 reports isLoading=false for a *disabled*
  // query (it stays pending, never fetches), which would read as a settled empty window.
  const windowSettled = jobsLoaded;
  const {
    recipients: totalRecipients,
    sent: emailsSent,
    skipped: emailsSkipped,
    failed: emailsFailed,
    completionPercentage,
  } = resolveCampaignTotals(campaign, windowSettled ? jobCounts : null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sending': return 'default';
      case 'completed': return 'secondary';
      case 'paused': return 'outline';
      case 'failed': return 'destructive';
      case 'draft': return 'secondary';
      case 'active': return 'default';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sending': return <Activity className="h-4 w-4" />;
      case 'completed': return <CheckCircle2 className="h-4 w-4" />;
      case 'paused': return <Pause className="h-4 w-4" />;
      case 'failed': return <AlertTriangle className="h-4 w-4" />;
      case 'draft': return <Clock className="h-4 w-4" />;
      case 'active': return <Activity className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // For now, no warnings since we're not using FastAPI safety system
  const hasWarnings = false;

  // Shared confirmation for the per-card "Delete Campaign" menu item. Rendered
  // in both the list and grid returns; the dialog itself is portalled so its
  // placement in the tree doesn't matter.
  const deleteConfirmDialog = (
    <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete campaign</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete &ldquo;{campaign.name}&rdquo;? This permanently
            removes the campaign and all of its queued and recipient records. This action
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => onDelete(campaign.id)}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (viewMode === 'list') {
    return (
      <>
      <Card className={`transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-primary' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => onSelect(campaign.id, checked as boolean)}
              />
              <div className="cursor-pointer" onClick={() => navigate(`/inbox/campaigns/${campaign.id}`)}>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg">{campaign.name}</h3>
                  {hasWarnings && (
                    <Badge variant="destructive" className="text-xs">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Warnings
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{campaign.subject_template}</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="font-medium">{totalRecipients}</div>
                  <div className="text-muted-foreground">Recipients</div>
                </div>
                <div className="text-center">
                  <div className="font-medium text-green-600">{emailsSent}</div>
                  <div className="text-muted-foreground">Sent</div>
                </div>
                {emailsSkipped > 0 && (
                  <div className="text-center" title="Recipients skipped because they previously unsubscribed, bounced, or marked email as spam.">
                    <div className="font-medium text-amber-600 flex items-center gap-1 justify-center">
                      <Ban className="h-3 w-3" />
                      {emailsSkipped}
                    </div>
                    <div className="text-muted-foreground">Skipped</div>
                  </div>
                )}
                <div className="text-center">
                  <div className="font-medium text-red-600">{emailsFailed}</div>
                  <div className="text-muted-foreground">Failed</div>
                </div>
              </div>

              <div className="w-32">
                <Progress value={completionPercentage} className="h-2" />
                <div className="text-xs text-center mt-1">{completionPercentage.toFixed(0)}%</div>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant={getStatusColor(campaign.status)} className="flex items-center gap-1">
                  {getStatusIcon(campaign.status)}
                  {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                </Badge>
                <CampaignControls campaignId={campaign.id} variant="minimal" showStatus={false} />
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/inbox/campaigns/${campaign.id}`)}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-red-600"
                    onSelect={() => setConfirmDeleteOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Campaign
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>
      {deleteConfirmDialog}
      </>
    );
  }

  // Grid view
  return (
    <>
    <Card className={`transition-all hover:shadow-md cursor-pointer ${isSelected ? 'ring-2 ring-primary' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onSelect(campaign.id, checked as boolean)}
              onClick={(e) => e.stopPropagation()}
            />
            <div onClick={() => navigate(`/inbox/campaigns/${campaign.id}`)}>
              <CardTitle className="text-base line-clamp-2">{campaign.name}</CardTitle>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/inbox/campaigns/${campaign.id}`)}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-red-600"
                onSelect={() => setConfirmDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Campaign
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent onClick={() => navigate(`/inbox/campaigns/${campaign.id}`)}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Badge variant={getStatusColor(campaign.status)} className="flex items-center gap-1">
              {getStatusIcon(campaign.status)}
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </Badge>
            {hasWarnings && (
              <Badge variant="destructive" className="text-xs">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Warnings
              </Badge>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>{completionPercentage.toFixed(0)}%</span>
            </div>
            <Progress value={completionPercentage} className="h-2" />
          </div>

          <div className={`grid gap-2 text-sm ${emailsSkipped > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <div className="text-center">
              <div className="font-medium">{totalRecipients}</div>
              <div className="text-muted-foreground text-xs">Recipients</div>
            </div>
            <div className="text-center">
              <div className="font-medium text-green-600">{emailsSent}</div>
              <div className="text-muted-foreground text-xs">Sent</div>
            </div>
            {emailsSkipped > 0 && (
              <div className="text-center" title="Recipients skipped because they previously unsubscribed, bounced, or marked email as spam.">
                <div className="font-medium text-amber-600 flex items-center gap-1 justify-center">
                  <Ban className="h-3 w-3" />
                  {emailsSkipped}
                </div>
                <div className="text-muted-foreground text-xs">Skipped</div>
              </div>
            )}
            <div className="text-center">
              <div className="font-medium text-red-600">{emailsFailed}</div>
              <div className="text-muted-foreground text-xs">Failed</div>
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            Created {format(new Date(campaign.created_at || Date.now()), 'MMM d, yyyy')}
          </div>

          <div onClick={(e) => e.stopPropagation()}>
            <CampaignControls campaignId={campaign.id} variant="compact" showStatus={false} />
          </div>
        </div>
      </CardContent>
    </Card>
    {deleteConfirmDialog}
    </>
  );
}

// A running campaign is 'active' (DB CHECK has no 'scheduled'; 'sending' is allowed but
// nothing writes it). Matches get_campaign_stats' active_campaigns definition.
const isActiveCampaign = (c: { status: string }) => ['active', 'sending'].includes(c.status);

export function CampaignManagementPage() {
  const setCampaignWizard = useSetAtom(campaignWizardAtom);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'status' | 'progress'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  // Snapshot of count + sent-count captured when the bulk-delete dialog opens, so
  // the dialog text stays correct during the ~200ms close animation (handleBulkAction
  // clears the selection on confirm, which would otherwise flash "Delete 0 campaigns?").
  const [bulkDeleteInfo, setBulkDeleteInfo] = useState<{ count: number; sentCount: number }>({
    count: 0,
    sentCount: 0,
  });

  const { data: campaigns = [], isLoading } = useHermesCampaigns();
  const updateCampaign = useUpdateHermesCampaign();
  const deleteCampaign = useDeleteHermesCampaign();
  const resumeCampaign = useResumeCampaign();
  const { toast } = useToast();

  const filteredAndSortedCampaigns = useMemo(() => {
    let filtered = campaigns.filter(campaign => {
      // Search filter
      if (searchTerm && !campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !campaign.subject_template?.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'all' && !(statusFilter === 'active' ? isActiveCampaign(campaign) : campaign.status === statusFilter)) {
        return false;
      }

      // Tab filter
      if (activeTab !== 'all') {
        switch (activeTab) {
          case 'active':
            return isActiveCampaign(campaign);
          case 'paused':
            return campaign.status === 'paused';
          case 'completed':
            return campaign.status === 'completed';
          case 'draft':
            return campaign.status === 'draft';
          default:
            return true;
        }
      }

      return true;
    });

    // Sort campaigns
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'progress':
          const aProgress = (a.emails_sent || 0) / (a.recipient_count || 1);
          const bProgress = (b.emails_sent || 0) / (b.recipient_count || 1);
          comparison = aProgress - bProgress;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [campaigns, searchTerm, statusFilter, sortBy, sortOrder, activeTab]);

  const handleSelectCampaign = (id: string, selected: boolean) => {
    const newSelected = new Set(selectedCampaigns);
    if (selected) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedCampaigns(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedCampaigns(new Set(filteredAndSortedCampaigns.map(c => c.id)));
    } else {
      setSelectedCampaigns(new Set());
    }
  };

  // Bulk Resume acts on paused campaigns only. The resume RPC also starts drafts, and
  // starting a draft is a single-campaign, confirmed action (CampaignControls' Start).
  const handleBulkResume = async () => {
    const paused = campaigns.filter((c) => selectedCampaigns.has(c.id) && c.status === 'paused');
    const skipped = selectedCampaigns.size - paused.length;
    setSelectedCampaigns(new Set());
    // Re-paces pending jobs from now (behind the active queue). Failures are toasted by the hook.
    const results = await Promise.allSettled(paused.map((c) => resumeCampaign.mutateAsync(c.id)));
    if (skipped > 0) {
      const resumed = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - resumed;
      toast({
        title: 'Bulk resume',
        description: `Resumed ${resumed}; skipped ${skipped} not paused${failed ? `; ${failed} failed` : ''}`,
      });
    }
  };

  const handleBulkAction = (action: 'pause' | 'delete') => {
    selectedCampaigns.forEach(campaignId => {
      switch (action) {
        case 'pause':
          updateCampaign.mutate({ 
            campaignId, 
            updates: { status: 'paused' } 
          });
          break;
        case 'delete':
          deleteCampaign.mutate(campaignId);
          break;
      }
    });
    setSelectedCampaigns(new Set());
  };

  const toggleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // How many of the selected campaigns have mailed recipients OR are live — used to
  // warn in the bulk-delete confirmation. We do NOT trust `emails_sent` alone (this
  // file treats that denormalized counter as unreliable and prefers per-job counts),
  // so a sending/active campaign mid-send isn't missed. Over-including is the safe
  // direction for a safety warning. draft/ready/scheduled are correctly excluded.
  const bulkSentCount = campaigns.filter(
    (c) =>
      selectedCampaigns.has(c.id) &&
      ((c.emails_sent || 0) > 0 ||
        ['sending', 'active', 'completed', 'paused', 'failed'].includes(c.status))
  ).length;

  const stats = {
    total: campaigns.length,
    active: campaigns.filter(isActiveCampaign).length,
    paused: campaigns.filter(c => c.status === 'paused').length,
    completed: campaigns.filter(c => c.status === 'completed').length,
    draft: campaigns.filter(c => c.status === 'draft').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Campaigns</h1>
          <p className="text-muted-foreground">
            Manage your email campaigns
          </p>
        </div>
        <Button onClick={() => {
                  track('campaign_wizard_opened', { source: 'campaigns_tab' });
                  setCampaignWizard({ open: true, selectedContacts: [] });
                }}>
          <Plus className="h-4 w-4 mr-2" />
          Create Campaign
        </Button>
      </div>


      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-muted-foreground">Total Campaigns</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                <div className="text-sm text-muted-foreground">Active</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Pause className="h-5 w-5 text-yellow-500" />
              <div>
                <div className="text-2xl font-bold text-yellow-600">{stats.paused}</div>
                <div className="text-sm text-muted-foreground">Paused</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-2xl font-bold text-blue-600">{stats.completed}</div>
                <div className="text-sm text-muted-foreground">Completed</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-500" />
              <div>
                <div className="text-2xl font-bold text-gray-600">{stats.draft}</div>
                <div className="text-sm text-muted-foreground">Draft</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-80"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                {sortOrder === 'asc' ? <SortAsc className="h-4 w-4 mr-2" /> : <SortDesc className="h-4 w-4 mr-2" />}
                Sort
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => toggleSort('name')}>
                Sort by Name
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => toggleSort('created_at')}>
                Sort by Date
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => toggleSort('status')}>
                Sort by Status
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => toggleSort('progress')}>
                Sort by Progress
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          {selectedCampaigns.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedCampaigns.size} selected
              </span>
              <Button variant="outline" size="sm" onClick={() => handleBulkAction('pause')}>
                <Pause className="h-4 w-4 mr-1" />
                Pause
              </Button>
              <Button variant="outline" size="sm" onClick={() => void handleBulkResume()}>
                <Play className="h-4 w-4 mr-1" />
                Resume
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBulkDeleteInfo({ count: selectedCampaigns.size, sentCount: bulkSentCount });
                  setBulkDeleteOpen(true);
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
          
          <div className="flex items-center border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
          <TabsTrigger value="active">Active ({stats.active})</TabsTrigger>
          <TabsTrigger value="paused">Paused ({stats.paused})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({stats.completed})</TabsTrigger>
          <TabsTrigger value="draft">Draft ({stats.draft})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Select All */}
          {filteredAndSortedCampaigns.length > 0 && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedCampaigns.size === filteredAndSortedCampaigns.length}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm text-muted-foreground">
                Select all {filteredAndSortedCampaigns.length} campaigns
              </span>
            </div>
          )}

          {/* Campaign List */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredAndSortedCampaigns.length > 0 ? (
            <div className={viewMode === 'grid' 
              ? 'grid gap-4 md:grid-cols-2 lg:grid-cols-3' 
              : 'space-y-4'
            }>
              {filteredAndSortedCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  isSelected={selectedCampaigns.has(campaign.id)}
                  onSelect={handleSelectCampaign}
                  onDelete={(id) => deleteCampaign.mutate(id)}
                  viewMode={viewMode}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No campaigns found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your filters to see more campaigns.'
                  : 'Create your first email campaign to get started.'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Button onClick={() => {
                  track('campaign_wizard_opened', { source: 'campaigns_tab' });
                  setCampaignWizard({ open: true, selectedContacts: [] });
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Campaign
                </Button>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {bulkDeleteInfo.count} campaign{bulkDeleteInfo.count === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes them and their queued recipients and can&rsquo;t be undone.
              {bulkDeleteInfo.sentCount > 0 && (
                <>
                  {' '}
                  {bulkDeleteInfo.sentCount} of these have already sent to recipients (or are
                  sending now); their send history and unsubscribe records will be kept.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleBulkAction('delete')}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}